import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import {
  Package,
  Plus,
  Search,
  ExternalLink,
  AlertTriangle,
  Archive,
  Eye,
  LayoutGrid,
  Table,
  ChevronDown,
  ChevronRight,
  Printer,
  Edit2,
  RotateCcw,
  Trash2
} from 'lucide-react';
import { useLabelQueue } from '@/contexts/LabelQueueContext';
import { useToast } from '@/components/ui/Toast';

interface ToolDefinition {
  id: string;
  name: string;
  description?: string;
  manufacturer?: string;
  part_number?: string;
  source_url?: string;
  unit_cost?: number;
  category_id: string;
  category_name?: string;
  category_icon?: string;
  is_restock_item: number;
  tracking_type: 'individual' | 'bin';
  min_quantity?: number;
  max_quantity?: number;
  quantity_on_hand: number;
  quantity_checked_out: number;
  archived: number;
}

interface Category {
  id: string;
  name: string;
  icon?: string;
}

interface ToolsResponse {
  items: ToolDefinition[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export function Inventory() {
  const queryClient = useQueryClient();
  const { addLabel } = useLabelQueue();
  const { addToast } = useToast();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState<ToolDefinition | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'category'>('category');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await api.get<Category[]>('/categories');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  // Fetch tools
  const { data: toolsData, isLoading } = useQuery({
    queryKey: ['tools', page, search, categoryFilter, showArchived],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: '25',
        archived: String(showArchived),
      });
      if (search) params.set('search', search);
      if (categoryFilter) params.set('categoryId', categoryFilter);

      const res = await api.get<ToolsResponse>(`/tools/definitions?${params}`);
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...(categories?.map(c => ({ value: c.id, label: c.name })) || []),
  ];

  // Group tools by category
  const toolsByCategory = toolsData?.items.reduce((acc, tool) => {
    const categoryId = tool.category_id;
    if (!acc[categoryId]) {
      acc[categoryId] = {
        categoryId,
        categoryName: tool.category_name || 'Uncategorized',
        tools: [],
      };
    }
    acc[categoryId].tools.push(tool);
    return acc;
  }, {} as Record<string, { categoryId: string; categoryName: string; tools: ToolDefinition[] }>);

  const categoryGroups = Object.values(toolsByCategory || {});

  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Create a new tool unit and add its label to print queue
  const handlePrintLabel = async (tool: ToolDefinition) => {
    // For both individual and bin-tracked items, create a new unit and get its label
    try {
      const res = await api.post<{ unitId: string; code: string; title: string; subtitle?: string }>(
        '/labels/create-unit',
        {
          definitionId: tool.id,
          isBinTracked: tool.tracking_type === 'bin'
        }
      );

      if (!res.success || !res.data?.code) {
        addToast({
          type: 'error',
          title: 'Failed to create unit',
          duration: 3000,
        });
        return;
      }

      addLabel({
        code: res.data.code,
        title: res.data.title,
        subtitle: res.data.subtitle,
        type: 'tool',
        id: res.data.unitId,
      });

      // Refresh the tools list to show new unit count
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      queryClient.invalidateQueries({ queryKey: ['tool-units', tool.id] });

      const message = tool.tracking_type === 'bin'
        ? 'Print the label and stick it on the bin storage location.'
        : 'Print the label and stick it on the tool.';

      addToast({
        type: 'success',
        title: 'Unit created & label queued',
        message,
        duration: 3000,
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Failed to create unit',
        duration: 3000,
      });
    }
  };

  // Calculate summary stats
  const totalTools = toolsData?.items.length || 0;
  const totalCategories = categoryGroups.length;
  const lowStockCount = toolsData?.items.filter(t =>
    t.min_quantity && t.quantity_on_hand <= t.min_quantity
  ).length || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Inventory</h1>
          <p className="text-foreground-muted">Manage your tool inventory</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex bg-surface rounded-lg p-1 border border-border">
            <button
              onClick={() => setViewMode('category')}
              className={`px-3 py-1.5 rounded text-sm flex items-center gap-1.5 transition-colors ${
                viewMode === 'category'
                  ? 'bg-primary text-white'
                  : 'text-foreground-muted hover:text-foreground'
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
              Category
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 rounded text-sm flex items-center gap-1.5 transition-colors ${
                viewMode === 'table'
                  ? 'bg-primary text-white'
                  : 'text-foreground-muted hover:text-foreground'
              }`}
            >
              <Table className="h-4 w-4" />
              Table
            </button>
          </div>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4" />
            Add Tool
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {viewMode === 'category' && !isLoading && totalTools > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardBody className="text-center">
              <p className="text-3xl font-bold text-primary">{totalTools}</p>
              <p className="text-sm text-foreground-muted">Total Tools</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <p className="text-3xl font-bold text-primary">{totalCategories}</p>
              <p className="text-sm text-foreground-muted">Categories</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <p className="text-3xl font-bold text-error">{lowStockCount}</p>
              <p className="text-sm text-foreground-muted">Low Stock</p>
            </CardBody>
          </Card>
        </div>
      )}

      <Card>
        <CardBody>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
                <Input
                  placeholder="Search tools..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="w-48">
              <Select
                options={categoryOptions}
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => {
                  setShowArchived(e.target.checked);
                  setPage(1);
                }}
                className="rounded border-border"
              />
              Show Archived
            </label>
          </div>

          {/* Tools List */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="spinner h-8 w-8 mx-auto" />
            </div>
          ) : toolsData?.items.length === 0 ? (
            <div className="text-center py-12 text-foreground-muted">
              <Package className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No tools found</p>
              <p className="text-sm">
                {search || categoryFilter
                  ? 'Try adjusting your filters'
                  : 'Add your first tool to get started'}
              </p>
            </div>
          ) : viewMode === 'category' ? (
            <div className="space-y-3">
              {categoryGroups.map(({ categoryId, categoryName, tools }) => {
                const isExpanded = expandedCategories.has(categoryId);
                const categoryLowStock = tools.filter(t =>
                  t.min_quantity && t.quantity_on_hand <= t.min_quantity
                ).length;

                return (
                  <Card key={categoryId}>
                    <CardBody className="p-0">
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(categoryId)}
                        className="w-full flex items-center justify-between p-4 hover:bg-surface-hover transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5 text-foreground-muted" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-foreground-muted" />
                          )}
                          <h3 className="font-semibold text-lg">{categoryName}</h3>
                          <Badge variant="neutral">{tools.length} tools</Badge>
                          {categoryLowStock > 0 && (
                            <Badge variant="error">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {categoryLowStock} low stock
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-foreground-muted">
                          {tools.reduce((sum, t) => sum + t.quantity_on_hand, 0)} on hand
                        </div>
                      </button>

                      {/* Category Tools */}
                      {isExpanded && (
                        <div className="border-t border-border">
                          <div className="overflow-x-auto">
                            <table className="table">
                              <thead>
                                <tr>
                                  <th>Tool</th>
                                  <th>Part #</th>
                                  <th className="text-right">On Hand</th>
                                  <th className="text-right">Checked Out</th>
                                  <th className="text-right">Unit Cost</th>
                                  <th>Status</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {tools.map((tool) => (
                                  <tr key={tool.id}>
                                    <td>
                                      <div>
                                        <p className="font-medium">{tool.name}</p>
                                        {tool.manufacturer && (
                                          <p className="text-xs text-foreground-muted">{tool.manufacturer}</p>
                                        )}
                                      </div>
                                    </td>
                                    <td className="font-mono text-sm">{tool.part_number || '-'}</td>
                                    <td className="text-right">
                                      <span className={
                                        tool.min_quantity && tool.quantity_on_hand <= tool.min_quantity
                                          ? 'text-error font-medium'
                                          : ''
                                      }>
                                        {tool.quantity_on_hand}
                                      </span>
                                      {tool.min_quantity && tool.quantity_on_hand <= tool.min_quantity && (
                                        <AlertTriangle className="inline h-4 w-4 ml-1 text-error" />
                                      )}
                                    </td>
                                    <td className="text-right">{tool.quantity_checked_out}</td>
                                    <td className="text-right">
                                      {tool.unit_cost
                                        ? `$${tool.unit_cost.toFixed(2)}`
                                        : '-'}
                                    </td>
                                    <td>
                                      {tool.archived ? (
                                        <Badge variant="neutral">
                                          <Archive className="h-3 w-3 mr-1" />
                                          Archived
                                        </Badge>
                                      ) : tool.is_restock_item ? (
                                        <Badge variant="success">Restock</Badge>
                                      ) : (
                                        <Badge variant="info">Non-Restock</Badge>
                                      )}
                                    </td>
                                    <td>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => setSelectedTool(tool)}
                                          title="View details"
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => handlePrintLabel(tool)}
                                          title="Print label"
                                        >
                                          <Printer className="h-4 w-4" />
                                        </Button>
                                        {tool.source_url && (
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => window.open(tool.source_url, '_blank')}
                                            title="Open source URL"
                                          >
                                            <ExternalLink className="h-4 w-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table table-fixed">
                  <colgroup>
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '14%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '11%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Tool</th>
                      <th>Category</th>
                      <th>Part #</th>
                      <th className="text-right">On Hand</th>
                      <th className="text-right">Checked Out</th>
                      <th className="text-right">Unit Cost</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {toolsData?.items.map((tool) => (
                      <tr key={tool.id}>
                        <td>
                          <div>
                            <p className="font-medium">{tool.name}</p>
                            {tool.manufacturer && (
                              <p className="text-xs text-foreground-muted">{tool.manufacturer}</p>
                            )}
                          </div>
                        </td>
                        <td>
                          <Badge variant="neutral">{tool.category_name}</Badge>
                        </td>
                        <td className="font-mono text-sm">{tool.part_number || '-'}</td>
                        <td className="text-right">
                          <span className={
                            tool.min_quantity && tool.quantity_on_hand <= tool.min_quantity
                              ? 'text-error font-medium'
                              : ''
                          }>
                            {tool.quantity_on_hand}
                          </span>
                          {tool.min_quantity && tool.quantity_on_hand <= tool.min_quantity && (
                            <AlertTriangle className="inline h-4 w-4 ml-1 text-error" />
                          )}
                        </td>
                        <td className="text-right">{tool.quantity_checked_out}</td>
                        <td className="text-right">
                          {tool.unit_cost
                            ? `$${tool.unit_cost.toFixed(2)}`
                            : '-'}
                        </td>
                        <td>
                          {tool.archived ? (
                            <Badge variant="neutral">
                              <Archive className="h-3 w-3 mr-1" />
                              Archived
                            </Badge>
                          ) : tool.is_restock_item ? (
                            <Badge variant="success">Restock</Badge>
                          ) : (
                            <Badge variant="info">Non-Restock</Badge>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setSelectedTool(tool)}
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePrintLabel(tool)}
                              title="Print label"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            {tool.source_url && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => window.open(tool.source_url, '_blank')}
                                title="Open source URL"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {toolsData && toolsData.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-foreground-muted">
                    Showing {((page - 1) * 25) + 1} to {Math.min(page * 25, toolsData.total)} of {toolsData.total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page >= toolsData.totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Add Tool Modal */}
      <AddToolModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        categories={categories || []}
        onSuccess={() => {
          setShowAddModal(false);
          queryClient.invalidateQueries({ queryKey: ['tools'] });
        }}
      />

      {/* Tool Detail Modal */}
      {selectedTool && (
        <ToolDetailModal
          tool={selectedTool}
          onClose={() => setSelectedTool(null)}
          onUpdate={() => {
            queryClient.invalidateQueries({ queryKey: ['tools'] });
          }}
        />
      )}
    </div>
  );
}

// Add Tool Modal Component
interface AddToolModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: Category[];
  onSuccess: () => void;
}

function AddToolModal({ isOpen, onClose, categories, onSuccess }: AddToolModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    description: '',
    manufacturer: '',
    partNumber: '',
    sourceUrl: '',
    unitCost: '',
    expectedLifeHours: '',
    isRestockItem: true,
    trackingType: 'individual' as 'individual' | 'bin',
    minQuantity: '',
    maxQuantity: '',
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await api.post('/tools/definitions', {
        name: data.name,
        categoryId: data.categoryId,
        description: data.description || undefined,
        manufacturer: data.manufacturer || undefined,
        partNumber: data.partNumber || undefined,
        sourceUrl: data.sourceUrl || undefined,
        unitCost: data.unitCost ? parseFloat(data.unitCost) : undefined,
        expectedLifeHours: data.expectedLifeHours ? parseFloat(data.expectedLifeHours) : undefined,
        isRestockItem: data.isRestockItem,
        trackingType: data.trackingType,
        minQuantity: data.minQuantity ? parseInt(data.minQuantity) : undefined,
        maxQuantity: data.maxQuantity ? parseInt(data.maxQuantity) : undefined,
      });
      if (!res.success) {
        const errorMsg = typeof res.error === 'string' ? res.error : JSON.stringify(res.error);
        throw new Error(errorMsg);
      }
      return res.data;
    },
    onSuccess: () => {
      setFormData({
        name: '',
        categoryId: '',
        description: '',
        manufacturer: '',
        partNumber: '',
        sourceUrl: '',
        unitCost: '',
        expectedLifeHours: '',
        isRestockItem: true,
        trackingType: 'individual',
        minQuantity: '',
        maxQuantity: '',
      });
      setError('');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to add tool');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.categoryId) {
      setError('Name and Category are required');
      return;
    }
    mutation.mutate(formData);
  };

  const categoryOptions = categories.map(c => ({ value: c.id, label: c.name }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Tool"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            Add Tool
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Tool Name *"
            value={formData.name}
            onChange={(e) => setFormData(d => ({ ...d, name: e.target.value }))}
            placeholder="e.g., 1/2 End Mill 4-Flute"
          />
          <Select
            label="Category *"
            options={categoryOptions}
            value={formData.categoryId}
            onChange={(e) => setFormData(d => ({ ...d, categoryId: e.target.value }))}
            placeholder="Select category"
          />
        </div>

        <Input
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData(d => ({ ...d, description: e.target.value }))}
          placeholder="Optional description"
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Manufacturer"
            value={formData.manufacturer}
            onChange={(e) => setFormData(d => ({ ...d, manufacturer: e.target.value }))}
            placeholder="e.g., Kennametal"
          />
          <Input
            label="Part Number"
            value={formData.partNumber}
            onChange={(e) => setFormData(d => ({ ...d, partNumber: e.target.value }))}
            placeholder="e.g., KC7140"
          />
        </div>

        <Input
          label="Source URL"
          type="url"
          value={formData.sourceUrl}
          onChange={(e) => setFormData(d => ({ ...d, sourceUrl: e.target.value }))}
          placeholder="https://supplier.com/product/..."
        />

        <div className="grid grid-cols-3 gap-4">
          <Input
            label="Unit Cost ($)"
            type="number"
            step="0.01"
            min="0"
            value={formData.unitCost}
            onChange={(e) => setFormData(d => ({ ...d, unitCost: e.target.value }))}
            placeholder="0.00"
          />
          <Input
            label="Expected Life (hrs)"
            type="number"
            step="0.1"
            min="0"
            value={formData.expectedLifeHours}
            onChange={(e) => setFormData(d => ({ ...d, expectedLifeHours: e.target.value }))}
            placeholder="0"
          />
          <div /> {/* Spacer */}
        </div>

        <div className="border-t border-border pt-4 mt-4">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Tracking Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="trackingType"
                  value="individual"
                  checked={formData.trackingType === 'individual'}
                  onChange={() => setFormData(d => ({ ...d, trackingType: 'individual' }))}
                  className="rounded-full border-border"
                />
                <span className="text-sm">Individual</span>
                <span className="text-xs text-foreground-muted">(Each unit gets its own label)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="trackingType"
                  value="bin"
                  checked={formData.trackingType === 'bin'}
                  onChange={() => setFormData(d => ({ ...d, trackingType: 'bin' }))}
                  className="rounded-full border-border"
                />
                <span className="text-sm">Bin</span>
                <span className="text-xs text-foreground-muted">(Bulk quantity, label on location)</span>
              </label>
            </div>
          </div>

          <label className="flex items-center gap-2 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isRestockItem}
              onChange={(e) => setFormData(d => ({ ...d, isRestockItem: e.target.checked }))}
              className="rounded border-border"
            />
            <span className="text-sm font-medium">Restock Item</span>
            <span className="text-xs text-foreground-muted">(Track inventory levels and get low stock alerts)</span>
          </label>

          {formData.isRestockItem && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Min Quantity"
                type="number"
                min="0"
                value={formData.minQuantity}
                onChange={(e) => setFormData(d => ({ ...d, minQuantity: e.target.value }))}
                placeholder="Alert when below this"
              />
              <Input
                label="Max Quantity"
                type="number"
                min="0"
                value={formData.maxQuantity}
                onChange={(e) => setFormData(d => ({ ...d, maxQuantity: e.target.value }))}
                placeholder="Target stock level"
              />
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

// Tool Detail Modal Component
interface ToolDetailModalProps {
  tool: ToolDefinition;
  onClose: () => void;
  onUpdate: () => void;
}

interface ToolUnit {
  id: string;
  label_code?: string;
  quantity?: number;
  status: string;
  condition?: string;
  location_name?: string;
  toolroom_name?: string;
  current_life_hours?: number;
  created_at: string;
  definition_name?: string;
  part_number?: string;
  manufacturer?: string;
}

interface Location {
  id: string;
  name: string;
  toolroom_name?: string;
}

function ToolDetailModal({ tool, onClose, onUpdate }: ToolDetailModalProps) {
  const queryClient = useQueryClient();
  const { addLabel } = useLabelQueue();
  const { addToast } = useToast();
  const [showAddStock, setShowAddStock] = useState(false);
  const [addStockQty, setAddStockQty] = useState('1');
  const [addStockLocation, setAddStockLocation] = useState('');
  const [addStockError, setAddStockError] = useState('');

  // Print label for a single unit
  const handlePrintUnitLabel = (unit: ToolUnit) => {
    const code = unit.label_code || unit.id;
    addLabel({
      code,
      title: tool.name,
      subtitle: tool.manufacturer || tool.part_number || undefined,
      location: unit.location_name
        ? `${unit.toolroom_name || ''} > ${unit.location_name}`
        : undefined,
      type: 'tool',
      id: unit.id,
    });
    addToast({
      type: 'info',
      title: 'Added to print queue',
      duration: 2000,
    });
  };

  // Fetch tool units for this definition
  const { data: units, isLoading: unitsLoading } = useQuery({
    queryKey: ['tool-units', tool.id],
    queryFn: async () => {
      const res = await api.get<ToolUnit[]>(`/tools/definitions/${tool.id}/units`);
      if (res.success) return res.data || [];
      return [];
    },
  });

  // Fetch locations for adding stock
  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const res = await api.get<Location[]>('/locations');
      if (res.success) return res.data || [];
      return [];
    },
    enabled: showAddStock,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const archiveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/tools/definitions/${tool.id}/archive`, {});
      if (!res.success) throw new Error(res.error);
    },
    onSuccess: () => {
      onUpdate();
      onClose();
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/tools/definitions/${tool.id}/unarchive`, {});
      if (!res.success) throw new Error(res.error);
    },
    onSuccess: () => {
      onUpdate();
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await api.delete(`/tools/definitions/${tool.id}`);
      if (!res.success) throw new Error(res.error);
    },
    onSuccess: () => {
      onUpdate();
      onClose();
    },
  });

  const addStockMutation = useMutation({
    mutationFn: async () => {
      const qty = parseInt(addStockQty);
      if (isNaN(qty) || qty < 1) throw new Error('Invalid quantity');
      if (!addStockLocation) throw new Error('Please select a location');

      const res = await api.post(`/tools/definitions/${tool.id}/units`, {
        quantity: qty,
        locationId: addStockLocation,
      });
      if (!res.success) throw new Error(res.error || 'Failed to add stock');
      return res.data;
    },
    onSuccess: () => {
      setShowAddStock(false);
      setAddStockQty('1');
      setAddStockLocation('');
      setAddStockError('');
      queryClient.invalidateQueries({ queryKey: ['tool-units', tool.id] });
      onUpdate();
    },
    onError: (err) => {
      setAddStockError(err instanceof Error ? err.message : 'Failed to add stock');
    },
  });

  const locationOptions = [
    { value: '', label: 'Select location...' },
    ...(locations?.map(l => ({
      value: l.id,
      label: l.toolroom_name ? `${l.toolroom_name} > ${l.name}` : l.name,
    })) || []),
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <Badge variant="success">Available</Badge>;
      case 'checked_out':
        return <Badge variant="warning">Checked Out</Badge>;
      case 'maintenance':
        return <Badge variant="info">Maintenance</Badge>;
      case 'damaged':
        return <Badge variant="error">Damaged</Badge>;
      case 'discarded':
        return <Badge variant="neutral">Discarded</Badge>;
      default:
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={tool.name}
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            {/* Delete - only for tools with no units and no checkout history */}
            {tool.quantity_on_hand === 0 && tool.quantity_checked_out === 0 && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                loading={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            )}
            {/* Archive/Unarchive */}
            {tool.archived ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => unarchiveMutation.mutate()}
                loading={unarchiveMutation.isPending}
              >
                <RotateCcw className="h-4 w-4" />
                Restore
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => archiveMutation.mutate()}
                loading={archiveMutation.isPending}
              >
                <Archive className="h-4 w-4" />
                Archive
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!tool.archived && !isEditing && (
              <Button variant="secondary" onClick={() => setIsEditing(true)}>
                <Edit2 className="h-4 w-4" />
                Edit
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-foreground-muted uppercase tracking-wide">Category</p>
            <p className="font-medium">{tool.category_name}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-muted uppercase tracking-wide">Part Number</p>
            <p className="font-medium font-mono">{tool.part_number || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-muted uppercase tracking-wide">Manufacturer</p>
            <p className="font-medium">{tool.manufacturer || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-muted uppercase tracking-wide">Unit Cost</p>
            <p className="font-medium">{tool.unit_cost ? `$${tool.unit_cost.toFixed(2)}` : '-'}</p>
          </div>
        </div>

        {tool.description && (
          <div>
            <p className="text-xs text-foreground-muted uppercase tracking-wide mb-1">Description</p>
            <p className="text-sm">{tool.description}</p>
          </div>
        )}

        {tool.source_url && (
          <div>
            <p className="text-xs text-foreground-muted uppercase tracking-wide mb-1">Source</p>
            <a
              href={tool.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline text-sm flex items-center gap-1"
            >
              {tool.source_url}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}

        {/* Stock Info */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Stock Information</h3>
            {!tool.archived && (
              <Button size="sm" onClick={() => setShowAddStock(!showAddStock)}>
                <Plus className="h-4 w-4" />
                Add Stock
              </Button>
            )}
          </div>

          {/* Add Stock Form */}
          {showAddStock && (
            <div className="bg-surface-hover rounded-lg p-4 mb-4">
              <h4 className="font-medium mb-3">Add New Tool Units</h4>
              {addStockError && (
                <div className="p-2 mb-3 text-sm text-error bg-error/10 rounded">{addStockError}</div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <Input
                  label="Quantity"
                  type="number"
                  min="1"
                  value={addStockQty}
                  onChange={(e) => setAddStockQty(e.target.value)}
                />
                <div className="col-span-2">
                  <Select
                    label="Storage Location"
                    options={locationOptions}
                    value={addStockLocation}
                    onChange={(e) => setAddStockLocation(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="ghost" size="sm" onClick={() => setShowAddStock(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => addStockMutation.mutate()}
                  loading={addStockMutation.isPending}
                >
                  Add Units
                </Button>
              </div>
            </div>
          )}

          {/* Stock Summary */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="bg-surface-hover rounded-lg p-3 text-center">
              <p className="text-2xl font-bold">{tool.quantity_on_hand}</p>
              <p className="text-xs text-foreground-muted">On Hand</p>
            </div>
            <div className="bg-surface-hover rounded-lg p-3 text-center">
              <p className="text-2xl font-bold">{tool.quantity_checked_out}</p>
              <p className="text-xs text-foreground-muted">Checked Out</p>
            </div>
            <div className="bg-surface-hover rounded-lg p-3 text-center">
              <p className="text-2xl font-bold">{tool.min_quantity || '-'}</p>
              <p className="text-xs text-foreground-muted">Min Qty</p>
            </div>
            <div className="bg-surface-hover rounded-lg p-3 text-center">
              <p className="text-2xl font-bold">{tool.max_quantity || '-'}</p>
              <p className="text-xs text-foreground-muted">Max Qty</p>
            </div>
          </div>

          {/* Individual Units List */}
          <div>
            <h4 className="font-medium mb-2">Individual Units</h4>
            {unitsLoading ? (
              <div className="text-center py-4">
                <div className="spinner h-6 w-6 mx-auto" />
              </div>
            ) : units && units.length > 0 ? (
              <div className="max-h-60 overflow-y-auto border border-border rounded-lg">
                <table className="table text-sm">
                  <thead className="sticky top-0 bg-surface">
                    <tr>
                      <th>Label</th>
                      <th>Qty</th>
                      <th>Status</th>
                      <th>Location</th>
                      <th>Life (hrs)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {units.map((unit) => (
                      <tr key={unit.id}>
                        <td className="font-mono text-xs">
                          {unit.label_code || unit.id.slice(0, 8)}
                        </td>
                        <td className="text-center font-medium">
                          {unit.quantity || 1}
                        </td>
                        <td>{getStatusBadge(unit.status)}</td>
                        <td className="text-xs">
                          {unit.location_name
                            ? `${unit.toolroom_name || ''} ${unit.toolroom_name ? '>' : ''} ${unit.location_name}`
                            : '-'}
                        </td>
                        <td>{unit.current_life_hours?.toFixed(1) || '0'}</td>
                        <td>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePrintUnitLabel(unit)}
                            title="Print label"
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-6 text-foreground-muted border border-border border-dashed rounded-lg">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No units in stock</p>
                <p className="text-xs">Click "Add Stock" to add tool units</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-surface rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="font-semibold text-lg mb-2">Delete Tool?</h3>
            <p className="text-sm text-foreground-muted mb-4">
              Are you sure you want to permanently delete "{tool.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  deleteMutation.mutate();
                  setShowDeleteConfirm(false);
                }}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tool Modal */}
      {isEditing && (
        <EditToolModal
          tool={tool}
          onClose={() => setIsEditing(false)}
          onSuccess={() => {
            setIsEditing(false);
            onUpdate();
          }}
        />
      )}
    </Modal>
  );
}

// Edit Tool Modal Component
interface EditToolModalProps {
  tool: ToolDefinition;
  onClose: () => void;
  onSuccess: () => void;
}

function EditToolModal({ tool, onClose, onSuccess }: EditToolModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: tool.name,
    description: tool.description || '',
    manufacturer: tool.manufacturer || '',
    partNumber: tool.part_number || '',
    sourceUrl: tool.source_url || '',
    unitCost: tool.unit_cost?.toString() || '',
    isRestockItem: !!tool.is_restock_item,
    trackingType: tool.tracking_type || 'individual',
    minQuantity: tool.min_quantity?.toString() || '',
    maxQuantity: tool.max_quantity?.toString() || '',
  });
  const [error, setError] = useState('');

  // Fetch categories for the select
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await api.get<Category[]>('/categories');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const [categoryId, setCategoryId] = useState(tool.category_id);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.put(`/tools/definitions/${tool.id}`, {
        name: formData.name,
        categoryId: categoryId,
        description: formData.description || undefined,
        manufacturer: formData.manufacturer || undefined,
        partNumber: formData.partNumber || undefined,
        sourceUrl: formData.sourceUrl || undefined,
        unitCost: formData.unitCost ? parseFloat(formData.unitCost) : undefined,
        isRestockItem: formData.isRestockItem,
        trackingType: formData.trackingType,
        minQuantity: formData.minQuantity ? parseInt(formData.minQuantity) : undefined,
        maxQuantity: formData.maxQuantity ? parseInt(formData.maxQuantity) : undefined,
      });
      if (!res.success) {
        const errorMsg = typeof res.error === 'string' ? res.error : JSON.stringify(res.error);
        throw new Error(errorMsg);
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to update tool');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      setError('Name is required');
      return;
    }
    mutation.mutate();
  };

  const categoryOptions = categories?.map(c => ({ value: c.id, label: c.name })) || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
      <div className="bg-surface rounded-lg max-w-xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-lg">Edit Tool</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Tool Name *"
              value={formData.name}
              onChange={(e) => setFormData(d => ({ ...d, name: e.target.value }))}
            />
            <Select
              label="Category *"
              options={categoryOptions}
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            />
          </div>

          <Input
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(d => ({ ...d, description: e.target.value }))}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Manufacturer"
              value={formData.manufacturer}
              onChange={(e) => setFormData(d => ({ ...d, manufacturer: e.target.value }))}
            />
            <Input
              label="Part Number"
              value={formData.partNumber}
              onChange={(e) => setFormData(d => ({ ...d, partNumber: e.target.value }))}
            />
          </div>

          <Input
            label="Source URL"
            type="url"
            value={formData.sourceUrl}
            onChange={(e) => setFormData(d => ({ ...d, sourceUrl: e.target.value }))}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Unit Cost ($)"
              type="number"
              step="0.01"
              min="0"
              value={formData.unitCost}
              onChange={(e) => setFormData(d => ({ ...d, unitCost: e.target.value }))}
            />
            <div /> {/* Spacer */}
          </div>

          <div className="border-t border-border pt-4 mt-4">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Tracking Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="editTrackingType"
                    value="individual"
                    checked={formData.trackingType === 'individual'}
                    onChange={() => setFormData(d => ({ ...d, trackingType: 'individual' }))}
                    className="rounded-full border-border"
                  />
                  <span className="text-sm">Individual</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="editTrackingType"
                    value="bin"
                    checked={formData.trackingType === 'bin'}
                    onChange={() => setFormData(d => ({ ...d, trackingType: 'bin' }))}
                    className="rounded-full border-border"
                  />
                  <span className="text-sm">Bin</span>
                </label>
              </div>
            </div>

            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isRestockItem}
                onChange={(e) => setFormData(d => ({ ...d, isRestockItem: e.target.checked }))}
                className="rounded border-border"
              />
              <span className="text-sm font-medium">Restock Item</span>
            </label>

            {formData.isRestockItem && (
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Min Quantity"
                  type="number"
                  min="0"
                  value={formData.minQuantity}
                  onChange={(e) => setFormData(d => ({ ...d, minQuantity: e.target.value }))}
                />
                <Input
                  label="Max Quantity"
                  type="number"
                  min="0"
                  value={formData.maxQuantity}
                  onChange={(e) => setFormData(d => ({ ...d, maxQuantity: e.target.value }))}
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
